<template>
  <div>
    <!-- Header Card -->
    <div class="card bg-primary text-white mb-4">
      <h5 class="card-header mb-0">{{ $t('Webportal.Actions.InquiryWizard.Step5.Title') }}</h5>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingCheckData" class="text-center my-5">
      <b-spinner variant="primary" :label="$t('Webportal.Loading')"></b-spinner>
      <p class="mt-3 text-muted">{{ $t('Webportal.Loading') }}...</p>
    </div>

    <template v-else>
      <!-- Counter Cards -->
      <div class="row mb-4">
        <div class="col-md-6 mb-3 mb-md-0">
          <SummaryCard :count="selectedQuestionIds.length" :title="$t('Webportal.Actions.InquiryWizard.Step5.QuestionsCount')" />
        </div>
        <div class="col-md-6">
          <SummaryCard :count="totalAttachmentsCount" :title="$t('Webportal.Actions.InquiryWizard.Step5.AttachmentsCount')" />
        </div>
      </div>

      <!-- Info Alert -->
      <b-alert show variant="info" class="mb-4">
        <font-awesome-icon :icon="['fal', 'info-circle']" class="mr-2" />
        <span class="small">{{ $t('Webportal.Actions.InquiryWizard.Step5.InfoAlert') }}</span>
      </b-alert>

      <!-- Question Review Cards -->
      <b-card v-for="(question, index) in questionReviewCards" :key="question.SurveyQuestionId" class="mb-4" no-body>
        <!-- Question Header -->
        <b-card-header class="bg-white">
          <div class="d-flex align-items-start">
            <div class="question-number-circle d-flex align-items-center justify-content-center mr-3 flex-shrink-0">
              <strong>{{ index + 1 }}</strong>
            </div>
            <div>
              <div class="d-flex align-items-center mb-1">
                <span class="font-weight-bold mr-2">{{ question.QuestionNumber }}</span>
                <b-badge variant="secondary">{{ question.ClusterText }}</b-badge>
              </div>
              <p class="mb-0">{{ question.QuestionText }}</p>
            </div>
          </div>
        </b-card-header>

        <b-card-body>
          <!-- Score Comparison: Shopper vs Dealer -->
          <div class="row mb-3">
            <!-- Shopper Score -->
            <div class="col-md-6 mb-2 mb-md-0">
              <QuestionResult :result="question.Result" :selected-answers="question.SelectedAnswers" :comment="question.CommentByShopper" />
            </div>
            <!-- Dealer Desired Score -->
            <div class="col-md-6">
              <QuestionResult :title="$t('Webportal.Actions.InquiryWizard.Step5.YourAnswer')" variant="success" :selected-answers="question.complaint.desiredAnswers" />
            </div>
          </div>

          <!-- Justification (collapsible) -->
          <CollapsibleInfo
            icon-name="comment-alt"
            :title="$t('Webportal.Actions.InquiryWizard.Step5.Justification')"
            :description="question.complaint.justification"
            :show-toggle="true"
            class="mb-3"
          />

          <!-- Attachments Section -->
          <div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="font-weight-bold small">
                <font-awesome-icon :icon="['fal', 'paperclip']" class="mr-1" />
                {{ $t('Webportal.Actions.InquiryWizard.Step5.EvidenceCount', { count: question.complaint.attachments.length }) }}
              </div>
              <AttachmentDropZone
                v-if="!isRetryMode"
                :simple="true"
                @attachments-added="handleAttachmentsAdded(question.SurveyQuestionId, $event)"
              />
            </div>

            <!-- Attachment List with Upload Status -->
            <AttachmentList
              :attachments="question.complaint.attachments"
              :editable="!isRetryMode && !isSubmitting"
              :deletable="!isRetryMode && !isSubmitting"
              @attachment-deleted="handleAttachmentDeleted(question.SurveyQuestionId, $event)"
              @attachment-updated="handleAttachmentUpdated(question.SurveyQuestionId, $event)"
            />
            <div v-if="question.complaint.attachments.length === 0" class="small text-muted">{{ $t('Webportal.Actions.InquiryWizard.Step5.NoEvidence') }}</div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Confirmations Section -->
      <b-card class="mb-4" v-if="!isRetryMode">
        <h5 class="mb-3">{{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmationsTitle') }}</h5>

        <div class="mb-3">
          <b-form-checkbox
            :checked="confirmations.correctness"
            @change="setConfirmation({ key: 'correctness', value: $event })"
            :disabled="isSubmitting"
          >
            <strong class="text-dark">{{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmCorrectness') }}</strong>
            <div class="small text-muted">
              {{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmCorrectnessHint') }}
            </div>
          </b-form-checkbox>
        </div>

        <div class="mb-3">
          <b-form-checkbox
            :checked="confirmations.privacy"
            @change="setConfirmation({ key: 'privacy', value: $event })"
            :disabled="isSubmitting"
          >
            <strong class="text-dark">{{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmPrivacy') }}</strong>
            <a href="/dataprotection" target="_blank"><font-awesome-icon :icon="['fal', 'external-link']" class="ml-1" /></a>
            <div class="small text-muted">
              {{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmPrivacyHint') }}
            </div>
          </b-form-checkbox>
        </div>

        <div>
          <b-form-checkbox
            :checked="confirmations.noChanges"
            @change="setConfirmation({ key: 'noChanges', value: $event })"
            :disabled="isSubmitting"
          >
            <strong class="text-dark">{{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmNoChanges') }}</strong>
            <div class="small text-muted">
              {{ $t('Webportal.Actions.InquiryWizard.Step5.ConfirmNoChangesHint') }}
            </div>
          </b-form-checkbox>
        </div>
      </b-card>

      <!-- Warning Alert -->
      <b-card header-bg-variant="warning" class="mb-4" v-if="!isRetryMode">
        <template #header>
          <div class="d-flex align-items-center">
            <font-awesome-icon :icon="['fal', 'exclamation-triangle']" class="mr-2" />
            <h6 class="mb-0 font-weight-bold">{{ $t('Webportal.Actions.InquiryWizard.Step5.WarningTitle') }}</h6>
          </div>
        </template>
        <p class="mb-0 small">
          {{ $t('Webportal.Actions.InquiryWizard.Step5.WarningText') }}
        </p>
      </b-card>

      <!-- Submission Progress -->
      <div v-if="isSubmitting" class="text-center my-4">
        <b-spinner variant="primary" />
        <p class="mt-2">{{ submissionProgress }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ActionService from "@/services/ActionService";
import QuestionResult from "./QuestionResult.vue";
import AttachmentDropZone from "./AttachmentDropZone.vue";
import AttachmentList from "./AttachmentList.vue";
import CollapsibleInfo from "./CollapsibleInfo.vue";
import SummaryCard from "@/components/SummaryCard.vue";
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";

export default {
  name: "Step5Review",

  mixins: [SurveyRevisionMixin],

  components: {
    QuestionResult,
    AttachmentDropZone,
    AttachmentList,
    CollapsibleInfo,
    SummaryCard,
  },

  data() {
    return {
      isLoadingCheckData: false,
      checkData: null,
      isSubmitting: false,
      submissionProgress: "",
      isRetryMode: false,
    };
  },

  computed: {
    ...mapGetters("inquiryWizard", [
      "selectedCheckId",
      "selectedQuestionIds",
      "getQuestionComplaint",
      "confirmations",
      "allConfirmationsChecked",
      "totalAttachmentsCount",
      "canSubmit",
      "hasUploadFailures",
      "createdSurveyRevisionId",
      "createdEntryMappings",
      "failedAttachmentsCount",
    ]),

    selectedQuestions() {
      if (!this.checkData || !this.checkData.Questions) {
        return [];
      }

      const questionsWithDetails = this.checkData.Questions.map((question) => {
        const comment = this.checkData.Comments?.find((c) =>
          question.IsCommentPossible
            ? c.QuestionNumber == question.QuestionNumber
            : question.QuestionNumber && c.QuestionNumber.startsWith(question.QuestionNumber)
        );

        const selectedAnswers =
          this.checkData.Answers?.filter((a) => a.SurveyQuestionId === question.SurveyQuestionId && a.IsAnswerSelected === true) || [];

        return {
          ...question,
          CommentByShopper: comment ? comment.CommentByShopper : null,
          SelectedAnswers: selectedAnswers,
        };
      });

      return questionsWithDetails.filter((q) => this.selectedQuestionIds.includes(q.SurveyQuestionId));
    },

    questionReviewCards() {
      return this.selectedQuestions.map((question) => {
        const complaint = this.getQuestionComplaint(question.SurveyQuestionId);
        return {
          ...question,
          complaint: {
            desiredAnswers: complaint.desiredAnswers || [],
            justification: complaint.justification || "",
            attachments: complaint.attachments || [],
          },
        };
      });
    },

    canSubmitInquiry() {
      return this.canSubmit && this.selectedQuestions.length > 0;
    },
  },

  watch: {
    canSubmitInquiry(newVal) {
      this.$emit("update-next-button-disabled", !newVal);
    },
  },

  created() {
    this.loadCheckData();

    // Check if returning to Step 5 after partial failure
    if (this.hasUploadFailures && this.createdSurveyRevisionId) {
      this.isRetryMode = true;
      this.$emit("update-back-button-disabled", true); // Disable back button in retry mode
      this.$emit('update-next-button-label', this.$t('Webportal.Actions.InquiryWizard.Step5.RetryUploads'));
      this.$emit('update-next-button-disabled', false);
    } else {
      this.$emit('update-back-button-label', this.$t('Webportal.Actions.InquiryWizard.Step5.BackAndEdit'));
      this.$emit('update-next-button-label', this.$t('Webportal.Actions.InquiryWizard.Step5.SubmitButton'));
      this.$emit("update-next-button-disabled", !this.canSubmitInquiry);
    }
  },

  methods: {
    ...mapActions("inquiryWizard", [
      "setConfirmation",
      "addQuestionAttachment",
      "updateQuestionAttachment",
      "removeQuestionAttachment",
      "resetWizard",
      "setAttachmentUploadStatus",
      "setHasUploadFailures",
      "setCreatedSurveyRevisionId",
      "setCreatedEntryMappings",
      "setSubmitting",
      "setSubmissionError",
    ]),

    async loadCheckData() {
      if (!this.selectedCheckId) {
        this.$router.push("/action/inquiry-wizard");
        return;
      }

      this.isLoadingCheckData = true;
      try {
        const response = await ActionService.getSurveyInstanceData(this.selectedCheckId, true, true, false);
        this.checkData = response.data;
      } catch (error) {
        console.error("Error loading check data:", error);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step5.LoadError'), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
      } finally {
        this.isLoadingCheckData = false;
      }
    },

    async handleAttachmentsAdded(questionId, attachments) {
      if (!attachments || attachments.length === 0) return;

      try {
        for (const attachment of attachments) {
          await this.addQuestionAttachment({
            questionId,
            attachment,
          });
        }
      } catch (error) {
        this.$bvToast.toast(error.message || this.$t('Webportal.Actions.InquiryWizard.Step4.AttachmentError'), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
      }
    },

    handleAttachmentDeleted(questionId, attachmentIndex) {
      this.removeQuestionAttachment({ questionId, attachmentIndex });
    },

    handleAttachmentUpdated(questionId, { index, attachment }) {
      this.updateQuestionAttachment({
        questionId,
        attachmentIndex: index,
        attachment,
      });
    },

    handleWizardBackClick() {
      if (this.isRetryMode) {
        // Cannot go back in retry mode — revision already created
        return false;
      }
      // Allow default back navigation to Step 4
      return true;
    },

    handleWizardNextClick() {
      this.submitInquiry();
      return false; // Prevent default wizard navigation
    },

    async submitInquiry() {
      if (this.isRetryMode) {
        // Retry only failed uploads
        await this.uploadAllAttachments();
        return;
      }

      this.isSubmitting = true;
      this.setSubmitting(true);
      this.submissionProgress = this.$t('Webportal.Actions.InquiryWizard.Step5.CreatingRevision');

      this.$emit("update-back-button-disabled", true); // Disable back button during submission

      try {
        // Build the SurveyRevision payload
        const payload = {
          SurveyInstanceId: this.selectedCheckId,
          StatusId: this.revisionInitialStatusId,
          Entries: this.selectedQuestions.map((q, index) => {
            const complaint = this.getQuestionComplaint(q.SurveyQuestionId);
            return {
              UniqueEntryId: index + 1,
              SurveyQuestionId: q.SurveyQuestionId,
              QuestionNumber: q.QuestionNumber,
              QuestionText: q.QuestionText,
              Remarks: complaint.justification,
              DesiredAnswers: complaint.desiredAnswers,
            };
          }),
        };

        // Create SurveyRevision
        const response = await ActionService.setSurveyRevision(payload);
        const surveyRevisionId = response.data.surveyRevisionId;
        const entries = response.data.entries ? response.data.entries : [];

        // Store in Vuex for retry
        this.setCreatedSurveyRevisionId(surveyRevisionId);
        this.setCreatedEntryMappings(entries);

        this.$bvToast.toast(
          this.$t("Webportal.SaveSuccess.Text", {
            entity: this.$t("Webportal.Actions.SurveyRevision"),
          }),
          {
            autoHideDelay: 2000,
            variant: "success",
            noCloseButton: true,
          }
        );

        // Upload attachments
        this.submissionProgress = this.$t('Webportal.Actions.InquiryWizard.Step5.UploadingAttachments');
        await this.uploadAllAttachments();
      } catch (error) {
        console.error("Error creating survey revision:", error);
        this.setSubmissionError(error.message);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step5.CreateError', { error: error.response?.data || error.message }), {
          title: this.$t('Webportal.Error'),
          variant: "danger",
        });
        this.isSubmitting = false;
        this.setSubmitting(false);
      }
    },

    async uploadAllAttachments() {
      this.isSubmitting = true;
      this.setSubmitting(true);
      this.submissionProgress = this.$t('Webportal.Actions.InquiryWizard.Step5.UploadingAttachments');

      const surveyRevisionId = this.createdSurveyRevisionId;
      const entryMappings = this.createdEntryMappings;
      let hasFailures = false;

      // Iterate all questions and their attachments
      for (const question of this.selectedQuestions) {
        const questionId = question.SurveyQuestionId;
        const complaint = this.getQuestionComplaint(questionId);

        if (!complaint.attachments || complaint.attachments.length === 0) continue;

        // Find the server entry ID for this question
        const questionIndex = this.selectedQuestions.findIndex((q) => q.SurveyQuestionId === questionId);
        const uniqueEntryId = questionIndex + 1;
        const entryMapping = entryMappings.find((e) => e.uniqueEntryId === uniqueEntryId);
        const surveyRevisionEntryId = entryMapping ? entryMapping.surveyRevisionEntryId : null;

        for (let attIndex = 0; attIndex < complaint.attachments.length; attIndex++) {
          const attachment = complaint.attachments[attIndex];

          // Skip already-successful uploads (retry mode)
          if (attachment.uploadStatus === "success") continue;

          // Set status to uploading
          this.setAttachmentUploadStatus({
            questionId,
            attachmentIndex: attIndex,
            uploadStatus: "uploading",
            uploadError: null,
          });

          this.submissionProgress = this.$t('Webportal.Actions.InquiryWizard.Step5.UploadingFile', { name: attachment.file.name });

          try {
            await ActionService.uploadSurveyRevisionAttachment(surveyRevisionId, surveyRevisionEntryId, attachment.file, {
              documentType: attachment.documentType,
              documentDate: attachment.documentDate || null,
              scope: attachment.scope || "question",
              shortDescription: attachment.shortDescription || null,
            });

            // Mark as success
            this.setAttachmentUploadStatus({
              questionId,
              attachmentIndex: attIndex,
              uploadStatus: "success",
              uploadError: null,
            });
          } catch (error) {
            hasFailures = true;
            const errorMsg = error.response?.data?.error || error.response?.data || error.message;
            this.setAttachmentUploadStatus({
              questionId,
              attachmentIndex: attIndex,
              uploadStatus: "failed",
              uploadError: typeof errorMsg === "string" ? errorMsg : this.$t('Webportal.Actions.InquiryWizard.Step5.UploadFailed'),
            });
          }
        }
      }

      this.isSubmitting = false;
      this.setSubmitting(false);

      if (hasFailures) {
        // Partial failure
        this.isRetryMode = true;
        this.setHasUploadFailures(true);
        this.$bvToast.toast(this.$t('Webportal.Actions.InquiryWizard.Step5.PartialUploadWarning'), {
          title: this.$t('Webportal.Actions.InquiryWizard.Step5.PartialUploadTitle'),
          variant: "warning",
        });
        this.$emit('update-next-button-label', this.$t('Webportal.Actions.InquiryWizard.Step5.RetryUploads'));
        this.$emit("update-next-button-disabled", false);
      } else {
        // Full success — navigate to success page
        this.resetWizard();
        this.$router.push({ name: "InquirySuccess" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.question-number-circle {
  border-radius: 50%;
  font-size: 1.25rem;
  width: 40px;
  height: 40px;
  min-width: 40px;
  background-color: var(--primary);
  color: white;
}
</style>
